import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { confirmResetPassword, resetPassword } from 'aws-amplify/auth';
import AuthShell from '../../components/auth/AuthShell.tsx';
import OtpInput from '../../components/auth/OtpInput.tsx';
import PasswordInput from '../../components/auth/PasswordInput.tsx';
import TextInput from '../../components/auth/TextInput.tsx';
import { humanizeAuthError } from '../../auth/errors.ts';
import './ForgotPassword.css';

type Step = 'request' | 'confirm';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleRequest(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (email.trim().length === 0 || submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await resetPassword({ username: email });
      // No user-enumeration: we always advance to the confirm step,
      // regardless of whether the email exists.
      setStep('confirm');
    } catch (err) {
      // Same principle — only show errors that aren't existence-related.
      // Cognito with PreventUserExistenceErrors hides "user not found"
      // already; this catch is for rate-limits / network issues.
      setError(humanizeAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (code.length !== 6 || password.length < 12 || submitting) {
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password,
      });
      navigate('/auth/signin', {
        replace: true,
        state: { email, passwordReset: true },
      });
    } catch (err) {
      setError(humanizeAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (step === 'request') {
    return (
      <AuthShell
        title="Reset password"
        subtitle="Self-service reset is currently unavailable — see the note below."
        footer={
          <span>
            Remembered it? <Link to="/auth/signin">Sign in</Link>
          </span>
        }
      >
        <div className="om-forgot__notice" role="note">
          <p>
            Because Email MFA protects every sign-in, Cognito won't let us
            issue password-reset codes by email — the same channel can't be
            both an MFA factor and a recovery path.
          </p>
          <p>
            Email <a href="mailto:admin@oculusmind.ai">admin@oculusmind.ai</a>{' '}
            from your registered address to request a manual reset. We're
            tracking self-service recovery as a follow-up; the form below
            remains in place for the day it becomes possible.
          </p>
        </div>
        <form
          className="om-forgot"
          onSubmit={(e) => {
            void handleRequest(e);
          }}
          noValidate
        >
          <TextInput
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
            disabled={submitting}
          />
          {error ? (
            <p className="om-forgot__error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            className="om-forgot__submit"
            disabled={email.trim().length === 0 || submitting}
            aria-disabled={email.trim().length === 0 || submitting}
          >
            {submitting ? 'Sending…' : 'Send reset code'}
          </button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Enter code and new password"
      subtitle={`If an account exists for ${email}, a 6-digit code is on the way.`}
      footer={
        <button
          type="button"
          className="om-forgot__back"
          onClick={() => {
            setStep('request');
            setCode('');
            setPassword('');
            setError(null);
          }}
          disabled={submitting}
        >
          ← Use a different email
        </button>
      }
    >
      <form
        className="om-forgot"
        onSubmit={(e) => {
          void handleConfirm(e);
        }}
        noValidate
      >
        <OtpInput
          label="Code"
          value={code}
          onChange={setCode}
          length={6}
          disabled={submitting}
          autoFocus
        />
        <PasswordInput
          label="New password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={12}
          disabled={submitting}
          hint="At least 12 characters. Mix upper, lower, digit, and symbol."
        />
        {error ? (
          <p className="om-forgot__error" role="alert">
            {error}
          </p>
        ) : null}
        <button
          type="submit"
          className="om-forgot__submit"
          disabled={code.length !== 6 || password.length < 12 || submitting}
          aria-disabled={code.length !== 6 || password.length < 12 || submitting}
        >
          {submitting ? 'Resetting…' : 'Reset password'}
        </button>
      </form>
    </AuthShell>
  );
}
